/**
 * IDB Storage Adapter — IndexedDB-backed persistent key-value store.
 *
 * Uses `idb-keyval` (600 bytes gzipped) for a dead-simple IndexedDB wrapper.
 *
 * WHY IndexedDB over sessionStorage?
 * - sessionStorage dies on tab close — users lose all cached data
 * - IndexedDB survives browser restarts, giving instant app loads
 * - 50MB+ quota vs 5-10MB for sessionStorage
 * - World-class apps (Linear, Notion, Figma) all use IndexedDB
 *
 * IMPORTANT: IndexedDB is async.  `createStore` needs synchronous getSnapshot(),
 * so we use a dual-layer strategy:
 * 1. On store creation, synchronously return initialValue (or warm cache from
 *    a boot-time preload — see `preloadKeys()`).
 * 2. Writes go to both in-memory state AND IndexedDB (async, fire-and-forget).
 * 3. On next app boot, `preloadKeys()` loads all persisted keys from IDB into
 *    an in-memory Map BEFORE any store is created — making hydration synchronous.
 *
 * This gives us the same synchronous API as sessionStorage while persisting
 * data across browser restarts.
 */

import { get, set, del, keys, createStore as createIdbStore } from 'idb-keyval';

// ── Custom IDB store (so we don't collide with other apps) ─────────────────
const pioneerDb = createIdbStore('pioneer-cache', 'key-value');

// ── In-memory mirror for synchronous reads ──────────────────────────────────
const memoryCache = new Map<string, string>();

/** Whether preload has completed */
let preloaded = false;

/**
 * Preload all keys from IndexedDB into the in-memory mirror.
 * Call this ONCE at app boot (before React renders) so that
 * `getItem()` returns cached data synchronously.
 *
 * Returns a promise that resolves when preloading is complete.
 * If called multiple times, subsequent calls are no-ops.
 */
let preloadPromise: Promise<void> | null = null;

export function preloadIdb(): Promise<void> {
  if (preloadPromise) return preloadPromise;

  preloadPromise = (async () => {
    try {
      const allKeys = await keys(pioneerDb);
      const entries = await Promise.all(
        allKeys.map(async (key) => {
          const value = await get<string>(key as IDBValidKey, pioneerDb);
          return [key as string, value] as const;
        }),
      );
      entries.forEach(([key, value]) => {
        if (value !== undefined) {
          memoryCache.set(key, value);
        }
      });
    } catch {
      // IndexedDB unavailable (incognito, storage disabled, etc.)
      // Fall through — stores will use initialValue, which is fine.
    }
    preloaded = true;
  })();

  return preloadPromise;
}

/** Check if preloading has completed. */
export function isPreloaded(): boolean {
  return preloaded;
}

// ── Adapter interface (matches SessionStorageAdapter) ───────────────────────
export interface IdbStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Persistent storage adapter backed by IndexedDB.
 *
 * - `getItem()` reads from the in-memory mirror (synchronous).
 * - `setItem()` writes to both the mirror AND IndexedDB (async write-through).
 * - `removeItem()` removes from both.
 * - `clear()` clears both.
 *
 * The in-memory mirror is populated by `preloadIdb()` at app boot.
 */
const idbStorage: IdbStorageAdapter = {
  getItem(key: string): string | null {
    return memoryCache.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    memoryCache.set(key, value);
    // Async write-through to IndexedDB (fire-and-forget)
    set(key, value, pioneerDb).catch(() => {
      // IndexedDB unavailable — in-memory still works for this session
    });
  },

  removeItem(key: string): void {
    memoryCache.delete(key);
    del(key, pioneerDb).catch(() => {
      // fail silently
    });
  },

  clear(): void {
    memoryCache.clear();
    // Clear all keys from our custom IDB store
    keys(pioneerDb)
      .then((allKeys) => Promise.all(allKeys.map((k) => del(k, pioneerDb))))
      .catch(() => {
        // fail silently
      });
  },
};

export default idbStorage;
